/* eslint-disable */
import { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation, useQuery } from '@tanstack/react-query';
import { BaseModal } from '../BaseModal'
import { modalActions } from '@/Actions';
import MultiCascaderTree from '@/Components/MultiCascaderTree/MultiCascaderTree';
import productCategoryServcies from '@/Services/Form Services/productCategoryServcies';
import productSubCategoryServcies from '@/Services/Form Services/productSubCategoryServcies';
import useProductMappingStore from '@/Pages/ProductMapping/productMappingStore';
import {
    buildDrillDownTree,
    mappingCategorized,
    modalHeaders,
    skuTypes
} from '@/Pages/ProductMapping/productMappigUtils';
import { LayoutPanelLeft, MousePointerClick, CheckCircle2, Layout, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { everyWordTitleCase } from '@/Helpers';
import { Button } from '@/Components/ui/button';
import productProcessMapServices from '@/Services/Analytics Services/product-process-map.services';
import { notificationServices } from '@/Services';

function ProductSelectionDrillDownModal(props) {
    const { show, data } = useSelector((state) => state?.modal?.productSelectionDrillDownModal);
    const productList = useSelector((state) => state?.form?.ALL_PRODUCTS?.data?.LIST);
    const dispatch = useDispatch();
    const [list, setList] = useState({});
    const [inputState, setInputState] = useState([]);
    const [selectedMappingValue, setSelectedMappingValue] = useState(null);
    const [expandedIds, setExpandedIds] = useState({});
    const productWiseMappingMap = {};
    const trackSelectionChange = useRef({});
    const {
        selectedChildTab,
        selectedDropDownValue,
        mappingDataValues,
        productWiseMappingData,
    } = useProductMappingStore();

    productWiseMappingData?.forEach((item) => {
        productWiseMappingMap[item?.mappingId] = { ...item };
    });

    const lastAutoExpandedRef = useRef('');

    useEffect(() => {
        const configKey = `${selectedChildTab?.id}-${selectedMappingValue?.id}`;
        if (inputState.length > 0 && lastAutoExpandedRef.current !== configKey) {
            const findSelectedPath = (nodes) => {
                for (const node of nodes) {
                    if (node.select === 1) {
                        return { [node.type]: node.id + '-' + node.type };
                    }
                    if (node.children?.length > 0) {
                        const childPath = findSelectedPath(node.children);
                        if (Object.keys(childPath).length > 0) {
                            return { [node.type]: node.id + '-' + node.type, ...childPath };
                        }
                    }
                }
                return {};
            };
            const initialExpanded = findSelectedPath(inputState);
            if (Object.keys(initialExpanded).length > 0) {
                setExpandedIds(initialExpanded);
                lastAutoExpandedRef.current = configKey;
            }
        }
    }, [inputState, selectedMappingValue, selectedChildTab]);

    const mappingValues = useMemo(() => {
        return mappingDataValues?.mappingTypeValues || [];
    }, [mappingDataValues]);

    const isBinaryMapping = useMemo(() => {
        if (mappingValues?.length !== 2) return false;
        const names = mappingValues.map(v => v.displayName?.toUpperCase());
        return names.includes('YES') && names.includes('NO');
    }, [mappingValues]);

    useEffect(() => {
        if (show && isBinaryMapping && !selectedMappingValue) {
            const yesVal = mappingValues.find(v => v.displayName?.toUpperCase() === 'YES');
            if (yesVal) {
                setSelectedMappingValue(yesVal);
            }
        }
    }, [show, isBinaryMapping, mappingValues, selectedMappingValue]);

    const { data: categories } = useQuery(
        ['productCategories'],
        () => productCategoryServcies.listProductCategory({ req: { params: { id: '' } } }),
        {
            enabled: show,
            staleTime: Infinity,
            refetchOnWindowFocus: false,
        }
    );

    const { data: subCategories } = useQuery(
        ['productSubCategories'],
        () => productSubCategoryServcies.listProductSubCategory({ req: { params: { id: '' } } }),
        {
            enabled: show,
            staleTime: Infinity,
            refetchOnWindowFocus: false,
        }
    );

    const { mutate: fetchMappingList, isLoading: fetchingMappingList } = useMutation({
        mutationFn: (payload) => {
            return productProcessMapServices.listProductProcess(payload);
        },
        onSuccess: (response) => {
            if (response?.responseCode === 'SS-001') {
                setList(response?.data);
            }
        }
    })

    const { mutateAsync: saveMapping, isLoading: isSaving } = useMutation({
        mutationFn: (payload) => productProcessMapServices.mapProductToProcess(payload),
    });

    const { mutateAsync: deleteMapping, isLoading: isDeleting } = useMutation({
        mutationFn: (payload) => productProcessMapServices.deleteProcessProductMap(payload),
    });


    useEffect(() => {
        if (show && selectedChildTab?.id) {
            fetchMappingList({
                data: {
                    storeId: selectedDropDownValue?.storeBased,
                    mappingType: selectedChildTab?.id,
                    storeTypeSubType: selectedDropDownValue?.storeTypeBased,
                }
            })
        }
    }, [show, selectedChildTab])

    useEffect(() => {
        if (show && categories?.data && subCategories?.data && productList && selectedMappingValue && Object.keys(list).length > 0) {
            const initialTableState = buildDrillDownTree({
                skuTypes,
                categories,
                subCategories,
                productList,
                selectedMappingValue,
                list,
                productWiseMappingMap,
                selectedChildTab,
                isBinaryMapping
            });
            setInputState(initialTableState);
        }
    }, [show, categories?.data, subCategories?.data, productList, selectedMappingValue, list, isBinaryMapping]);

    const [isConfigExpanded, setIsConfigExpanded] = useState(false);
    const displayedMappingValues = isConfigExpanded ? mappingValues : mappingValues.slice(0, 4);
    const hasMoreConfig = mappingValues.length > 4;

    const handleClose = () => {
        setSelectedMappingValue(null);
        trackSelectionChange.current = {};
        dispatch(modalActions.toggleProductSelectionDrillDownModal(false))
    }

    const getItemName = (id, type) => {
        const findNode = (nodes) => {
            for (const node of nodes) {
                if (String(node.id) === String(id) && node.type === type) return node.label || node.productName;
                if (node.children) {
                    const found = findNode(node.children);
                    if (found) return found;
                }
            }
            return null;
        };
        return findNode(inputState) || `Item ${id}`;
    };

    const handleSave = async () => {
        const isParentSelected = (id, type, nodes) => {
            const findPath = (targetId, targetType, currentNodes, path = []) => {
                for (const node of currentNodes) {
                    if (node.id === targetId && node.type === targetType) return path;
                    if (node.children) {
                        const result = findPath(targetId, targetType, node.children, [...path, node]);
                        if (result) return result;
                    }
                }
                return null;
            };

            const path = findPath(id, type, nodes);
            if (!path) return false;

            return path.some(ancestor => {
                const ancestorKey = `${ancestor.id}-${ancestor.type}`;
                const change = trackSelectionChange.current[ancestorKey];
                return change ? (change.select === 1) : (ancestor.select === 1);
            });
        };

        const toAddMap = {};
        const toDeleteIds = [];

        Object.entries(trackSelectionChange.current).forEach(([nodeKey, change]) => {
            const [id, type] = nodeKey.split('-');
            const mappingOn = type === 'root' ? mappingCategorized['skuType'] : mappingCategorized[type];
            const selectStatus = change.select;
            const mappingId = change?.mappingId;

            // Group Additions: Only add if selected (1) AND NO PARENT is selected
            if (selectStatus === 1) {
                if (!isParentSelected(id, type, inputState)) {
                    if (!toAddMap[mappingOn]) {
                        toAddMap[mappingOn] = {
                            mappingOn,
                            mappingOnElements: [],
                            mappingType: { id: selectedChildTab?.id },
                            mappingTypeElement: selectedMappingValue?.id,
                            ...(selectedChildTab?.storeBased && { store: { id: selectedDropDownValue?.storeBased } }),
                            ...(selectedChildTab?.storeTypeBased && {
                                storeTypeSubType: { id: selectedDropDownValue?.storeTypeBased }
                            })
                        };
                    }
                    if (!toAddMap[mappingOn].mappingOnElements.includes(Number(id))) {
                        toAddMap[mappingOn].mappingOnElements.push(Number(id));
                    }
                }
            }
            if (mappingId) {
                if (selectStatus === -1) {
                    toDeleteIds.push(mappingId);
                } else if (selectStatus === 0) {
                    if (!isParentSelected(id, type, inputState)) {
                        toDeleteIds.push(mappingId);
                    }
                }
            }
        });

        const toAdd = Object.values(toAddMap);

        const totalTasks = toDeleteIds.length + toAdd.length;
        if (totalTasks === 0) return handleClose();

        const results = {
            success: 0,
            failed: 0,
            errors: []
        };

        const delay = (ms) => new Promise(res => setTimeout(res, ms));

        // Process Deletions
        if (toDeleteIds.length > 0) {
            for (const mappingId of toDeleteIds) {
                try {
                    const res = await deleteMapping({ data: { id: mappingId } });
                    if (res?.statusCode === 200 || res?.status === 200) {
                        results.success++;
                    } else {
                        results.failed++;
                        results.errors.push(`Failed to delete mapping: ${res?.message || 'Unknown error'}`);
                    }
                } catch (err) {
                    results.failed++;
                    results.errors.push(`Error deleting mapping: ${err?.message || 'API Error'}`);
                }
                if (totalTasks > 1) await delay(2000);
            }
        }

        // Process Additions
        if (toAdd.length > 0) {
            for (const payload of toAdd) {
                try {
                    const res = await saveMapping({ data: payload });
                    if (res?.statusCode === 200 || res?.status === 200) {
                        results.success++;
                    } else {
                        results.failed++;
                        const itemType = payload.mappingOn;
                        results.errors.push(`Failed to map ${itemType}: ${res?.message || 'Unknown error'}`);
                    }
                } catch (err) {
                    results.failed++;
                    results.errors.push(`Error in mapping: ${err?.message || 'API Error'}`);
                }
                if (toAdd.indexOf(payload) !== toAdd.length - 1) await delay(2000);
            }
        }

        if (results.errors.length > 0) {
            results.errors.forEach(msg => {
                notificationServices.generateNotification({
                    type: 'error',
                    message: msg
                });
            });
        }

        if (results.success > 0) {
            notificationServices.generateNotification({
                type: 'success',
                message: `Successfully updated ${results.success} mapping sets.`
            });

            props.refreshData?.();

            handleClose();
        } else if (results.failed > 0) {
            notificationServices.generateNotification({
                type: 'warning',
                message: 'All mapping updates failed.'
            });
        }
    }

    const renderBtns = () => {
        return (
            <div className='w-full flex justify-end items-center gap-2'>
                <Button
                    variant='outline'
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    className='min-w-[140px]'
                    disabled={isSaving || isDeleting}
                >
                    {(isSaving || isDeleting) ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Processing...
                        </>
                    ) : 'Apply Mapping'}
                </Button>
            </div>
        )
    }

    return (
        <BaseModal
            open={show}
            handleClose={handleClose}
            renderBtns={renderBtns}
            width='80rem'
            height='50rem'
            heading='Digital Factory - Add Products to Mapping'
        >
            <div className='flex flex-col h-full bg-slate-50/10'>
                {/* Status Legend & Mapping Type Selection */}
                <div className='flex flex-col bg-white border-b border-slate-200 overflow-hidden'>
                    {/* Status Legend Row */}
                    <div className='flex flex-wrap items-center gap-x-8 gap-y-3 px-6 py-3 bg-slate-50/30 border-b border-slate-100'>
                        <div className='flex items-center gap-2.5'>
                            <div className='w-5 h-5 rounded bg-rose-500 flex items-center justify-center text-white text-[10px] font-bold shadow-sm'>
                                ✕
                            </div>
                            <span className='text-[11px] text-slate-600 leading-tight font-medium'>
                                Unmapped <span className='text-slate-400 font-normal'>(Child mapping remains)</span>
                            </span>
                        </div>

                        <div className='flex items-center gap-2.5'>
                            <div className='w-5 h-5 rounded bg-green-500 flex items-center justify-center text-white text-[10px] font-bold shadow-sm'>
                                ✓
                            </div>
                            <span className='text-[11px] text-slate-600 leading-tight font-medium'>
                                Mapped <span className='text-slate-400 font-normal'>(Applied globally)</span>
                            </span>
                        </div>

                        <div className='flex items-center gap-2.5'>
                            <div className='w-5 h-5 rounded border-2 border-slate-200 bg-white shadow-sm' />
                            <span className='text-[11px] text-slate-600 font-medium'>
                                Inherit <span className='text-slate-400 font-normal'>(Follows parent)</span>
                            </span>
                        </div>

                        <div className='flex items-center gap-4 ml-auto border-l border-slate-200 pl-6 h-6'>
                            <div className='flex items-center gap-2'>
                                <div className='w-2.5 h-2.5 rounded-full bg-green-600 shadow-sm' />
                                <span className='text-[11px] text-slate-500 font-medium'>Active Mapped</span>
                            </div>
                            <div className='flex items-center gap-2'>
                                <div className='w-2.5 h-2.5 rounded-full bg-red-600 shadow-sm' />
                                <span className='text-[11px] text-slate-500 font-medium'>Active Unmapped</span>
                            </div>
                        </div>
                    </div>

                    {/* Mapping Type Selection Row */}
                    {!isBinaryMapping && (
                        <div className='px-6 py-4 flex flex-col gap-4'>
                            <div className='flex items-center justify-between'>
                                <div className='flex items-center gap-2 text-slate-400'>
                                    <LayoutPanelLeft size={18} />
                                    <span className='text-[13px] font-bold uppercase tracking-wider text-slate-500'>
                                        Mapping Config:
                                    </span>
                                </div>
                                {selectedMappingValue && (
                                    <div className='font-medium text-[12px] bg-amber-50 text-amber-700 px-3 py-1 rounded border border-amber-100 animate-in fade-in slide-in-from-right-2 duration-300'>
                                        ⚡ Configure products for <span className='font-black uppercase'>{selectedMappingValue.displayName}</span>
                                    </div>
                                )}
                            </div>
                            <div className='flex flex-wrap gap-2.5'>
                                {displayedMappingValues.map((val) => {
                                    const isSelected = selectedMappingValue?.id === val.id;
                                    return (
                                        <div
                                            key={val.id}
                                            onClick={() => setSelectedMappingValue(val)}
                                            className={`
                                                group relative flex items-center gap-2.5 px-4 py-2 rounded-lg cursor-pointer transition-all duration-200 border-2
                                                ${isSelected
                                                    ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-md shadow-blue-100 scale-[1.02]'
                                                    : 'bg-white border-slate-100 text-slate-500 hover:border-slate-300 hover:bg-slate-50'
                                                }
                                            `}
                                        >
                                            <div className={`
                                                w-5 h-5 rounded-full flex items-center justify-center transition-colors
                                                ${isSelected ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200'}
                                            `}>
                                                <CheckCircle2 size={12} strokeWidth={3} />
                                            </div>
                                            <span className='text-[13px] font-bold'>
                                                {everyWordTitleCase(val.displayName)}
                                            </span>
                                            {isSelected && (
                                                <div className='absolute -top-1 -right-1 flex h-3 w-3'>
                                                    <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75'></span>
                                                    <span className='relative inline-flex rounded-full h-3 w-3 bg-blue-500'></span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {hasMoreConfig && (
                                    <div
                                        onClick={() => setIsConfigExpanded(!isConfigExpanded)}
                                        className='flex items-center gap-1.5 px-3 py-2 rounded-lg cursor-pointer transition-all duration-200 border-2 border-dashed border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-500 bg-slate-50/50'
                                    >
                                        <span className='text-[11px] font-bold uppercase tracking-tight'>
                                            {isConfigExpanded ? 'Show Less' : `+${mappingValues.length - 4} More`}
                                        </span>
                                        {isConfigExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className='flex-1 p-4 overflow-y-auto'>
                    {selectedMappingValue ? (
                        <div className='h-full animate-in fade-in slide-in-from-bottom-4 duration-500 bg-white rounded-xl shadow-inner border border-slate-200 p-2'>
                            <MultiCascaderTree
                                inputState={inputState}
                                setInputState={setInputState}
                                headers={modalHeaders}
                                externalExpandedIds={expandedIds}
                                trackSelectionChange={(node, obj) => {
                                    const [id, type] = node.split('-');
                                    trackSelectionChange.current = {
                                        ...trackSelectionChange.current,
                                        [node]: {
                                            ...obj?.mappedData,
                                            id: id,
                                            select: obj?.select
                                        }
                                    };
                                }}
                            />
                        </div>
                    ) : (
                        <div className='h-full flex flex-col items-center justify-center gap-6 bg-white/50 rounded-2xl border-2 border-dashed border-slate-200 animate-pulse'>
                            <div className='relative'>
                                <div className='absolute -inset-4 bg-blue-500/10 rounded-full blur-xl' />
                                <div className='relative p-6 bg-white rounded-3xl shadow-xl ring-1 ring-slate-200'>
                                    <MousePointerClick size={48} className='text-blue-500' />
                                </div>
                                <div className='absolute -bottom-2 -right-2 p-2 bg-amber-400 rounded-lg shadow-lg rotate-12'>
                                    <Layout size={16} className='text-white' />
                                </div>
                            </div>
                            <div className='text-center space-y-2 max-w-sm'>
                                <h3 className='text-xl font-black text-slate-800 tracking-tight'>
                                    Guided Setup Initialized
                                </h3>
                                <p className='text-[14px] text-slate-500 font-medium leading-relaxed px-10'>
                                    Please select a <span className='text-blue-500 font-bold'>Mapping Choice</span> from the list above to begin product assignment.
                                </p>
                            </div>
                            <div className='flex gap-1.5'>
                                {[1, 2, 3].map(i => (
                                    <div key={i} className='w-1.5 h-1.5 rounded-full bg-slate-300 animate-bounce' style={{ animationDelay: `${i * 150}ms` }} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </BaseModal>
    )
}
export default ProductSelectionDrillDownModal